// externals
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { ACTIVITY_NAME_VEHICLE_PROTECTION } from '@makemydeal/dr-dpm-client';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { Action } from '@interstate/components/Action';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import * as calculationDetailsSelectors from '@makemydeal/dr-dash-store/selectors/calculationDetailsSelectors';
import * as pushToDarwinActionCreators from '@makemydeal/dr-dash-store/actions/pushToDarwinActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import { featureToggleSelectors, dealerSelectors } from '@makemydeal/dr-shared-store';
import DealActivityCard from '../DealActivityCard.interstate';
import { getCurrentOfferType } from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { OFFER_TYPE } from '@makemydeal/dr-platform-types';

const name = 'vppActivityVehicleProtection';

const VppActivityVehicleProtectionCard = () => {
    const dispatch = useDispatch();

    const completed = useSelector(vehicleProtectionSelectors.getVppSelectedProducts).length > 0;
    const { totalProductPrice } = useSelector(vehicleProtectionSelectors.getVppSummary);
    const isFieEngine = useSelector(calculationDetailsSelectors.isFieCalculationEngineService);
    const isDarwinEnabled = useSelector(dealerSelectors.isDarwinEnabled);
    const enablePushToDarwin = useSelector(featureToggleSelectors.isPushToDarwinEnabled) && isDarwinEnabled;
    const isVPPTemplateProductsLoading = useSelector(vehicleProtectionSelectors.isVPPTemplateProductsLoading);
    const isNotBalloonDeal = useSelector(getCurrentOfferType) !== OFFER_TYPE.balloon;
    const showOpenInDarwin = enablePushToDarwin && isNotBalloonDeal;

    const cardValue = formatDollarsAndCents(totalProductPrice);
    const vehicleProtectionDpmState = useSelector((state: StateTree) =>
        dealXgDetailsSelectors.getDpmStateByActivity(state, ACTIVITY_NAME_VEHICLE_PROTECTION)
    );

    if (!isFieEngine) {
        return null;
    }

    const redirectToFullVppPage = () => {
        if (completed) dispatch(interactionAnalyticsActionCreators.vehicleProtectionEditLink());
        else dispatch(interactionAnalyticsActionCreators.vehicleProtectionStartLink());

        dispatch(baseDealActionCreators.openSlideOut(baseDealActionCreators.SlideOutType.Protection));
    };

    const handleOpenInDarwin = () => {
        dispatch(pushToDarwinActionCreators.openInDarwin());
    };

    return (
        <DealActivityCard
            dpmState={vehicleProtectionDpmState}
            name={name}
            cardValue={cardValue}
            completed={completed}
            showCardAnchor={completed}
            action={redirectToFullVppPage}
            isLoading={isVPPTemplateProductsLoading}
            additionalAnchor={
                showOpenInDarwin && (
                    <Action
                        size="sm"
                        onClick={handleOpenInDarwin}
                        data-testid="vpp-activity-vehicle-protection-open-in-darwin-action"
                    >
                        Open in Darwin
                    </Action>
                )
            }
        />
    );
};

export default VppActivityVehicleProtectionCard;
